import { useState, useMemo, useEffect } from "react";
import { cn } from "@/lib/utils";
import { Estampado, PortadaConfig } from "./types";

interface PreviewCapasProps {
  portadaConfig: PortadaConfig;
  cubiertaUrl: string;
  nombrePersonalizado?: string;
  orientacion: "horizontal" | "vertical";
  estampadosDisponibles: Estampado[];
  className?: string;
  onClick?: () => void;
}

export const PreviewCapas = ({
  portadaConfig,
  cubiertaUrl,
  nombrePersonalizado,
  orientacion,
  estampadosDisponibles,
  className,
  onClick,
}: PreviewCapasProps) => {
  const [capaFondoCargada, setCapaFondoCargada] = useState(false);
  const [capaCubiertaCargada, setCapaCubiertaCargada] = useState(false);
  const [cubiertaDimensions, setCubiertaDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);

  // Obtener URL del fondo (estampado, imagen personalizada, o logo)
  const fondoUrl = useMemo(() => {
    if (portadaConfig.tipo === "estampado" && portadaConfig.estampado) {
      const estampado = estampadosDisponibles.find(
        (e) => e.id === portadaConfig.estampado,
      );
      return estampado?.path || null;
    }
    if (
      portadaConfig.tipo === "imagen-personalizada" &&
      portadaConfig.imagenPersonalizada
    ) {
      return portadaConfig.imagenPersonalizada;
    }
    if (portadaConfig.tipo === "logo-color" && portadaConfig.logo) {
      return portadaConfig.logo;
    }
    return null;
  }, [portadaConfig, estampadosDisponibles]);

  // Cargar dimensiones reales de la imagen de cubierta
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setCubiertaDimensions({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    };
    img.src = cubiertaUrl;
  }, [cubiertaUrl]);

  // Calcular aspect ratio dinámico basado en dimensiones reales
  // Solo usar cubiertaDimensions si coinciden con la orientacion esperada
  // (evita que dimensiones viejas de cubierta vertical bloqueen el cambio a horizontal)
  const cubiertaConcuerdaConOrientacion = cubiertaDimensions
    ? (cubiertaDimensions.width > cubiertaDimensions.height) === (orientacion === "horizontal")
    : false;
  const aspectRatio = cubiertaDimensions && cubiertaConcuerdaConOrientacion
    ? cubiertaDimensions.width / cubiertaDimensions.height
    : orientacion === "horizontal"
      ? 4 / 3
      : 9 / 16;

  const todasLasCapasCargadas = capaFondoCargada && capaCubiertaCargada;

  return (
    <div
      className={cn(
        "relative rounded-lg overflow-hidden border-2 border-brown-light/30",
        "flex items-center justify-center transition-all duration-300 ease-in-out",
        onClick &&
          "cursor-pointer hover:border-primary/40 hover:shadow-md touch-manipulation active:scale-[0.98]",
        className,
      )}
      onClick={onClick}
      style={{
        backgroundColor: "#e5e7eb",
        isolation: "isolate",
        aspectRatio: aspectRatio.toString(),
        width: "100%",
        maxWidth: "100%",
        height: "auto",
      }}
      role={onClick ? "button" : undefined}
      aria-label={onClick ? "Ver vista previa ampliada" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {/* CAPA 1: Fondo - puede ser estampado, imagen personalizada, o logo */}
      {fondoUrl && (
        <>
          {/* Color de fondo solo para tipo logo-color */}
          {portadaConfig.tipo === "logo-color" && portadaConfig.color && (
            <div
              className="absolute inset-0 w-full h-full"
              style={{
                backgroundColor: portadaConfig.color,
                zIndex: 5,
              }}
            />
          )}

          {/* Imagen (estampado, imagen personalizada, o logo) */}
          <img
            src={fondoUrl}
            alt={
              portadaConfig.tipo === "logo-color" ? "Logo" : "Fondo de portada"
            }
            className={cn(
              "absolute inset-0 w-full h-full transition-opacity duration-300",
              portadaConfig.tipo === "logo-color"
                ? "object-contain p-8" // Logo centrado con padding
                : "object-cover", // Estampados cubren todo
              todasLasCapasCargadas ? "opacity-100" : "opacity-0",
            )}
            style={{
              zIndex: 10,
              ...(portadaConfig.tipo !== "logo-color" && {
                transform: "scale(0.99) translateX(0.5%)",
                transformOrigin: "center",
              }),
            }}
            onLoad={() => setCapaFondoCargada(true)}
            onError={(e) => {
              console.error("Error cargando fondo:", fondoUrl);
              const target = e.currentTarget as HTMLImageElement;
              target.src =
                "https://images.unsplash.com/photo-1557682250-33bd709cbe85?w=400&h=500&fit=crop";
              setCapaFondoCargada(true);
            }}
          />
        </>
      )}

      {/* CAPA 2: Cubierta - el marco limita el estampado */}
      <img
        src={cubiertaUrl}
        alt="Cubierta de agenda"
        className={cn(
          "absolute inset-0 w-full h-full object-cover pointer-events-none transition-opacity duration-300",
          todasLasCapasCargadas ? "opacity-100" : "opacity-0",
        )}
        style={{
          zIndex: 20,
          mixBlendMode: "multiply",
          // multiply: Los colores claros/blancos del PNG se vuelven transparentes,
          // mientras que el marco oscuro se mantiene visible
        }}
        onLoad={() => setCapaCubiertaCargada(true)}
        onError={() => {
          console.error("Error cargando cubierta:", cubiertaUrl);
          setCapaCubiertaCargada(true);
        }}
      />

      {/* CAPA 3: Nombre personalizado */}
      {nombrePersonalizado && todasLasCapasCargadas && (
        <div
          className="absolute bottom-1.5 right-1.5 sm:bottom-2 sm:right-2 md:bottom-4 md:right-4 pointer-events-none"
          style={{ zIndex: 30 }}
        >
          <p className="font-serif text-xs sm:text-sm md:text-base text-brown-dark font-medium tracking-wide whitespace-nowrap px-1.5 py-1 sm:px-2 sm:py-1.5 md:px-3 md:py-2 drop-shadow-[0_2px_4px_rgba(255,255,255,0.9)] max-w-[200px] sm:max-w-[280px] md:max-w-none overflow-hidden text-ellipsis">
            {nombrePersonalizado}
          </p>
        </div>
      )}

      {/* Estado de carga */}
      {!todasLasCapasCargadas && fondoUrl && (
        <div
          className="absolute inset-0 flex items-center justify-center bg-brown-light/5"
          style={{ zIndex: 40 }}
          role="status"
          aria-live="polite"
        >
          <div className="text-center px-4">
            <div className="w-6 h-6 sm:w-8 sm:h-8 border-3 sm:border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-1.5 sm:mb-2" />
            <p className="text-[10px] sm:text-xs text-brown-light/60">
              Cargando...
            </p>
          </div>
        </div>
      )}

      {/* Placeholder cuando no hay fondo seleccionado */}
      {!fondoUrl && (
        <div
          className="absolute inset-0 flex items-center justify-center text-brown-light/40"
          style={{ zIndex: 5 }}
        >
          <p className="text-sm text-center px-4">Selecciona un diseño</p>
        </div>
      )}
    </div>
  );
};
